/**
 * Builds the app download menu: which links to show, and in what order.
 *
 * Filtering is scoped to the device class the visitor is browsing from. Someone
 * on a Mac may well carry an Android phone, so narrowing the desktop list to
 * macOS must not also narrow the mobile list — we can only infer the platform
 * of the device in front of us, never the other one.
 */

import { getDesktopAppDownloadUrls } from '../config/desktopAppConfig';
import { MOBILE_APP_STORE_URLS } from '../constants/mobileApps';
import { type DesktopPlatform, detectDesktopPlatform } from '../utils/desktopPlatform';
import { detectMobilePlatform, type MobilePlatform } from '../utils/mobilePlatform';

export type DeviceClass = 'desktop' | 'mobile';

/** Which bundled asset the Header should render alongside the link. */
export type AppDownloadIcon = 'apple' | 'windows' | 'googlePlay';

export interface AppDownloadLink {
  id: string;
  label: string;
  href: string;
  deviceClass: DeviceClass;
  platform: DesktopPlatform | MobilePlatform;
  icon: AppDownloadIcon;
}

function buildDesktopLinks(): AppDownloadLink[] {
  const urls = getDesktopAppDownloadUrls();

  return [
    {
      id: 'desktop-macos',
      label: 'Desktop app for Mac',
      href: urls.macos,
      deviceClass: 'desktop',
      platform: 'macos',
      icon: 'apple',
    },
    {
      id: 'desktop-windows',
      label: 'Desktop app for Windows',
      href: urls.windows,
      deviceClass: 'desktop',
      platform: 'windows',
      icon: 'windows',
    },
  ];
}

function buildMobileLinks(): AppDownloadLink[] {
  return [
    {
      id: 'mobile-ios',
      label: 'iOS app on App Store',
      href: MOBILE_APP_STORE_URLS.ios,
      deviceClass: 'mobile',
      platform: 'ios',
      icon: 'apple',
    },
    {
      id: 'mobile-android',
      label: 'Android app on Google Play',
      href: MOBILE_APP_STORE_URLS.android,
      deviceClass: 'mobile',
      platform: 'android',
      icon: 'googlePlay',
    },
  ];
}

/**
 * An empty match means we ship nothing for that platform — Linux, or anything
 * we failed to recognise — in which case the visitor gets every option and
 * picks for themselves.
 */
function narrowToPlatform(
  links: AppDownloadLink[],
  platform: DesktopPlatform | MobilePlatform | null,
): AppDownloadLink[] {
  const matches = links.filter((link) => link.platform === platform);
  return matches.length > 0 ? matches : links;
}

export function buildAppDownloadLinks(
  desktopPlatform: DesktopPlatform | null,
  mobilePlatform: MobilePlatform | null,
): AppDownloadLink[] {
  const onMobile = mobilePlatform !== null;

  const desktop = onMobile
    ? buildDesktopLinks()
    : narrowToPlatform(buildDesktopLinks(), desktopPlatform);
  const mobile = onMobile
    ? narrowToPlatform(buildMobileLinks(), mobilePlatform)
    : buildMobileLinks();

  return onMobile ? [...mobile, ...desktop] : [...desktop, ...mobile];
}

export function getAppDownloadLinks(): AppDownloadLink[] {
  return buildAppDownloadLinks(detectDesktopPlatform(), detectMobilePlatform());
}
